import React, { useState, useEffect } from 'react';
import { Fingerprint, Cpu, User, Clock, FileText, AlertCircle, Check, RefreshCw } from 'lucide-react';
import { API } from '../../App';

const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4';

export default function ProvenanceView({ frayId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await API.get(`/api/frays/${frayId}/provenance`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load provenance');
      }
      setLoading(false);
    };
    fetch();
  }, [frayId]);

  if (loading) return <div className="text-center py-10 text-[#A0A0A0] font-mono text-sm">Loading provenance...</div>;
  if (error) return (
    <div className={`${card} text-center`}>
      <AlertCircle className="w-6 h-6 mx-auto mb-2 text-[#A61E17]" />
      <p className="text-sm text-[#A0A0A0]">{error}</p>
    </div>
  );
  if (!data) return null;

  const prov = data.provenance || {};

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Fingerprint className="w-5 h-5 text-[#F2A900]" />
        <h3 className="text-sm font-bold font-['Barlow_Condensed'] text-[#F5F1E8] uppercase tracking-wider">Provenance Chain</h3>
        {prov.reconstructed && (
          <span className="px-2 py-0.5 bg-[#F2A900]/20 text-[#F2A900] text-[10px] font-mono rounded">RECONSTRUCTED</span>
        )}
      </div>

      {/* Fray metadata */}
      <div className={card}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <span className="text-[10px] font-mono text-[#A0A0A0] block">FRAY ID</span>
            <span className="text-xs font-mono text-[#00D4FF] break-all">{data.fray_id}</span>
          </div>
          <div>
            <span className="text-[10px] font-mono text-[#A0A0A0] block">TEMPLATE</span>
            <span className="text-xs font-mono text-[#F5F1E8]">{data.template || '—'}</span>
          </div>
          <div>
            <span className="text-[10px] font-mono text-[#A0A0A0] block">CREATED</span>
            <span className="text-xs font-mono text-[#F5F1E8]">{data.created_at ? new Date(data.created_at).toLocaleString() : '—'}</span>
          </div>
          <div>
            <span className="text-[10px] font-mono text-[#A0A0A0] block">UPDATED</span>
            <span className="text-xs font-mono text-[#F5F1E8]">{data.updated_at ? new Date(data.updated_at).toLocaleString() : '—'}</span>
          </div>
        </div>
      </div>

      {/* Models used */}
      {prov.models && prov.models.length > 0 && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#F2A900] mb-3 flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5" />MODELS USED
          </h4>
          <div className="flex flex-wrap gap-2">
            {prov.models.map((m, i) => (
              <span key={i} className="px-2 py-1 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-xs font-mono text-[#00D4FF]">
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Voices / Personas */}
      {prov.voices && prov.voices.length > 0 && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#F2A900] mb-3 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" />VOICES / PERSONAS
          </h4>
          <div className="flex flex-wrap gap-2">
            {prov.voices.map((v, i) => (
              <span key={i} className="px-2 py-1 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-xs font-mono text-[#F5F1E8]">
                {v}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Model versions */}
      {prov.model_versions && Object.keys(prov.model_versions).length > 0 && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#F2A900] mb-3">MODEL VERSIONS</h4>
          <div className="space-y-1">
            {Object.entries(prov.model_versions).map(([model, version]) => (
              <div key={model} className="flex justify-between text-xs font-mono">
                <span className="text-[#A0A0A0]">{model}</span>
                <span className="text-[#00D4FF]">{version}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prompt hashes */}
      {prov.prompt_hashes && Object.keys(prov.prompt_hashes).length > 0 && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#F2A900] mb-3 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" />PROMPT HASHES
          </h4>
          <div className="space-y-1">
            {Object.entries(prov.prompt_hashes).map(([phase, hash]) => (
              <div key={phase} className="flex justify-between text-xs font-mono">
                <span className="text-[#A0A0A0]">{phase}</span>
                <span className="text-[#F5F1E8] opacity-60 truncate ml-4">{hash}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Token usage */}
      {prov.token_counts && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#F2A900] mb-3">TOKEN USAGE</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <span className="text-lg font-bold font-mono text-[#00D4FF]">{(prov.token_counts.input || 0).toLocaleString()}</span>
              <span className="text-[10px] font-mono text-[#A0A0A0] block">INPUT</span>
            </div>
            <div>
              <span className="text-lg font-bold font-mono text-[#FF4D00]">{(prov.token_counts.output || 0).toLocaleString()}</span>
              <span className="text-[10px] font-mono text-[#A0A0A0] block">OUTPUT</span>
            </div>
            <div>
              <span className="text-lg font-bold font-mono text-[#F2A900]">{(prov.token_counts.total || 0).toLocaleString()}</span>
              <span className="text-[10px] font-mono text-[#A0A0A0] block">TOTAL</span>
            </div>
          </div>
        </div>
      )}

      {/* Cost */}
      {prov.cost && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#F2A900] mb-2">ESTIMATED COST</h4>
          <span className="text-xl font-bold font-mono text-[#F5F1E8]">${typeof prov.cost === 'number' ? prov.cost.toFixed(4) : prov.cost}</span>
        </div>
      )}

      {/* Decision lineage */}
      {prov.decision_lineage && prov.decision_lineage.length > 0 && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#F2A900] mb-3 flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" />DECISION LINEAGE
          </h4>
          <div className="space-y-2">
            {prov.decision_lineage.map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-6 h-6 rounded-full bg-[#FF4D00]/20 flex items-center justify-center text-[10px] font-mono text-[#FF4D00]">{i + 1}</div>
                  {i < prov.decision_lineage.length - 1 && <div className="w-px h-6 bg-[#2A2A2A]" />}
                </div>
                <div>
                  <span className="text-xs font-mono text-[#F5F1E8]">{step.phase || step.step}</span>
                  {step.outcome && <p className="text-xs text-[#A0A0A0]">{step.outcome}</p>}
                  {step.model && <span className="text-[10px] font-mono text-[#00D4FF]">{step.model}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Model usage stats from API */}
      {data.model_usage && data.model_usage.length > 0 && (
        <div className={card}>
          <h4 className="text-xs font-mono text-[#F2A900] mb-3">API USAGE BREAKDOWN</h4>
          <div className="space-y-1">
            {data.model_usage.map((u, i) => (
              <div key={i} className="flex justify-between text-xs font-mono">
                <span className="text-[#A0A0A0]">{u.status}</span>
                <span className="text-[#F5F1E8]">{u.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw provenance JSON (collapsible) */}
      <RawProvenance data={prov} />
    </div>
  );
}

function RawProvenance({ data }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={card}>
      <button onClick={() => setOpen(!open)} className="text-xs font-mono text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors">
        {open ? '▼' : '▶'} Raw Provenance JSON
      </button>
      {open && (
        <pre className="mt-2 text-[10px] font-mono text-[#A0A0A0] bg-[#0F0F0F] rounded p-3 overflow-auto max-h-60">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
